
import { Bot, Layers, FileText, GitMerge, Github, Cpu, Blocks, Library, Network, Database } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

export type Skill = {
  name: string;
  icon: string | ComponentType<SVGProps<SVGSVGElement>>;
};

export type SkillsCategory = {
  name: string;
  skills: Skill[];
};

export const skillsData: SkillsCategory[] = [
  {
    name: 'Programming Languages',
    skills: [
      { name: 'C#', icon: 'https://icon.icepanel.io/Technology/svg/C%23-%28CSharp%29.svg' },
      { name: 'C++', icon: 'https://icon.icepanel.io/Technology/svg/C%2B%2B-%28CPlusPlus%29.svg' },
      { name: 'Python', icon: 'https://icon.icepanel.io/Technology/svg/Python.svg' },
      { name: 'SQL', icon: 'https://icon.icepanel.io/Technology/svg/SQL-Developer.svg' },
      { name: 'Java', icon: 'https://icon.icepanel.io/Technology/svg/Java.svg' },
      { name: 'JavaScript', icon: 'https://icon.icepanel.io/Technology/svg/JavaScript.svg' },
      { name: 'OpenGL', icon: 'https://icon.icepanel.io/Technology/svg/OpenGL.svg' },
    ],
  },
  {
    name: 'Game Engines',
    skills: [
      { name: 'Unity', icon: 'https://icon.icepanel.io/Technology/png-shadow-512/Unity.png' },
      { name: 'Unreal Engine', icon: 'https://icon.icepanel.io/Technology/png-shadow-512/Unreal-Engine.png' },
      { name: 'pygame', icon: 'https://icon.icepanel.io/Technology/svg/Python.svg' },
    ],
  },
  {
    name: 'AI',
    skills: [
      { name: 'Agent development', icon: Bot },
      { name: 'RAG', icon: Layers },
      { name: 'Prompt engineering', icon: FileText },
      { name: 'Copilot', icon: 'https://icon.icepanel.io/Technology/png-shadow-512/GitHub-Codespaces.png' },
      { name: 'Cursor', icon: 'https://paulstamatiou.com/_next/image?url=%2Fgear%2Fcursor-app-icon.png&w=3840&q=75' }, // No icon on techicons, keeping local
      { name: 'Firebase Studio', icon: 'https://icon.icepanel.io/Technology/svg/Firebase.svg' }, // No icon on techicons, keeping local
    ],
  },
  {
    name: 'Web development',
    skills: [
      { name: 'HTML5', icon: 'https://icon.icepanel.io/Technology/svg/HTML5.svg' },
      { name: 'CSS3', icon: 'https://icon.icepanel.io/Technology/svg/CSS3.svg' },
      { name: 'jQuery', icon: 'https://icon.icepanel.io/Technology/svg/jQuery.svg' },
      { name: 'PHP', icon: 'https://icon.icepanel.io/Technology/svg/PHP.svg' },
      { name: 'Symfony', icon: 'https://icon.icepanel.io/Technology/png-shadow-512/Symfony.png' },
    ],
  },
  {
    name: 'Databases',
    skills: [
      { name: 'SQL Server', icon: 'https://icon.icepanel.io/Technology/png-shadow-512/Microsoft-SQL-Server.png' },
      { name: 'MySQL', icon: 'https://icon.icepanel.io/Technology/svg/MySQL.svg' },
      { name: 'PostgreSQL', icon: 'https://icon.icepanel.io/Technology/svg/PostgresSQL.svg' },
    ],
  },
  {
    name: 'Source Control',
    skills: [
      { name: 'Git', icon: 'https://icon.icepanel.io/Technology/svg/Git.svg' },
      { name: 'Perforce', icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Logo-perforce-icon-reg.svg/941px-Logo-perforce-icon-reg.svg.png' },
    ],
  },
  {
    name: 'CI/CD & Automation',
    skills: [
      { name: 'Docker', icon: 'https://icon.icepanel.io/Technology/svg/Docker.svg' },
      { name: 'GitHub Actions', icon: 'https://icon.icepanel.io/Technology/svg/GitHub-Actions.svg' },
      { name: 'n8n', icon: 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/n8n-icon.png' },
    ],
  },
  {
    name: 'Misc',
    skills: [
      { name: 'Multithreading', icon: Cpu },
      { name: 'Design Patterns', icon: Blocks },
      { name: 'Architecture', icon: Library },
      { name: 'Networking', icon: Network }
    ],
  }
];

export type Project = {
  id: string;
  title: string;
  technologies: string[];
  imagePlaceholderId: string;
  videoUrl?: string;
  description: string;
  longDescription: string;
  playUrl?: string;
  repoUrl?: string;
  downloadUrl?: string; // New optional field for download button

};

export const projectsData: Project[] = [
  {
    id: 'unsettlers',
    title: 'Unsettlers',
    technologies: ['Unity', 'C#'],
    imagePlaceholderId: 'unsettlers-thumbnail',
    description: 'Unsettlers is a game about exploration and resource management.',
    longDescription: 'All sprites and music are extracted from the game "The battle for Wesnoth", published under the GNU General Public License and downloadable at https://www.wesnoth.org/<br><br>Move the hero with ASDW and click on tiles with the mouse. <br><br>Build villages and bring peasants to them for gathering resources that allow you to explore further and find the hidden relics.<br><br>Collect gold to buy perks that will make the game progressively easier.<br><br>Find the hidden relics to win!',
    repoUrl: 'https://github.com/mvrey/Unsettlers',
    playUrl: 'https://mvrey.itch.io/unsettlers'
  },

  {
    id: 'shipwrecked',
    title: 'Shipwrecked',
    technologies: ['Unity', 'C#'],
    imagePlaceholderId: 'shipwrecked-thumbnail',
    description: 'A very metroidvania pirate game developed for "Metroidvania Month 17" on itch.io',
    longDescription: 'Try and fix your ship to go back to civilization!',
    repoUrl: 'https://github.com/mvrey/MetroidvaniaMonth17',
    playUrl: 'https://mvrey.itch.io/shipwrecked'
  },

  {
    id: 'simple-theseus',
    title: 'Ultimate Undead Obliteration 3',
    technologies: ['Unity', 'C#', 'Android', 'Mobile'],
    imagePlaceholderId: 'simple-theseus-thumbnail',
    description: 'A 3D run&shoot game developed with mobile interfaces in mind',
    longDescription: 'Defeat all the zombies and eliminate the spawners.<br><br>Mazes are generated procedurally for each run.<br><br>Try out 6 different weapons to obliterate the undead!',
    repoUrl: 'https://github.com/mvrey/SimpleTheseus',
    playUrl: 'https://mvrey.itch.io/simple-zombie-maze-3d'
  },
  {
    id: 'battle-for-pass',
    title: 'The battle for pass',
    technologies: ['C++', 'OpenGL'],
    imagePlaceholderId: 'battle-for-pass-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/DoxPsefKvhk',
    description: 'The Battle For Pass is an epic C++ RPG adventure. In it you must save the village of Pass from the evil dragon and its minions.',
    longDescription: 'You can select from 4 races and 4 professions for each race. This will determine the player stats and capabilities.<br><br>The maps are composed using Tiled and parsed through a C++ tmx interpreter. The game includes systems for magic, potions and combat, among others. Every character/profession combination has its set of images and sprites to form animations.',
    repoUrl: 'https://github.com/mvrey/The-battle-For-Pass'
  },
  {
    id: 'crimson-hawk',
    title: 'Crimson hawk',
    technologies: ['Flash', 'ActionScript'],
    imagePlaceholderId: 'crimson-hawk-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/O6bfS4L5Sco',
    description: 'A space shooter written using Flash ActionScript 3.',
    longDescription: 'Fight hordes of flying saucers and face 4 different end level bosses.<br><br>Buy different ships and improve their weaponry, shields and other assets. Survive for as long as you can!<br><br>The music is extracted and belongs to the Sega Megadrive game "Gynoug".',
    repoUrl: 'https://github.com/mvrey/Crimson-Hawk'
  },
  {
    id: 'flightsim',
    title: 'FlightSim',
    technologies: ['C++', 'OpenGL', 'Procedural generation', '3D game engine development'],
    imagePlaceholderId: 'flightsim-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/DHMebd7WTz8',
    description: 'FlightSim is an experiment on terrain procedural generation over an OpenGL framework.',
    longDescription: 'You control a ship and are free to roam around in a fully 3D environment. The terrain will generate and destroy as you aproach its borders, using an algorithm based on perlin noise of several octaves. This means that every time you run the program, the terrain will be different.',
    repoUrl: 'https://github.com/mvrey/FlightSim'
  },
  {
    id: 'lunar-lander',
    title: 'Lunar lander',
    technologies: ['C++', 'Chimpmunk physics', 'Procedural generation'],
    imagePlaceholderId: 'lunar-lander-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/EFLpQuMxiEs',
    description: 'An experiment that clones the classic Lunar Lander game.',
    longDescription: 'This project makes use of the chipmunk physics library for environment interaction, and perlin noise for a scrollable procedural-generated terrain.<br><br>Land your ship onto the moon and explore the surface with the Lunar Vehicle Rover.<br><br>Check out my other games, programming and game design stuff!',
    repoUrl: 'https://github.com/mvrey/LunarLander'
  },
  {
    id: 'asteroids',
    title: 'Asteroids',
    technologies: ['C', 'OpenGL'],
    imagePlaceholderId: 'asteroids-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/SvAn0pJUvHc',
    description: 'A clone of the famous 70s game Asteroids coded using C and OpenGL',
    longDescription: 'Asteroids is the first game developed using the MOMOS2D game engine, using C and OpenGL.<br><b>It forks the classic 70’s game adding persistent player registering and score keeping, as well as a credit system that increases as the player improves their previous high score.',
    repoUrl: 'https://github.com/mvrey/Asteroids'
  },
  {
    id: 'prison-escape-run',
    title: 'Prison escape run',
    technologies: ['C++', 'AI', 'Pathfinding', 'FSM'],
    imagePlaceholderId: 'prison-escape-run-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/A7TKDT2NNAY',
    description: 'Prison escape run is a simulation about autonomous deterministic software agents.',
    longDescription: 'Prisoners work while waiting for their chance to escape the fortress.Spies try to free the prisoners, and guards try to keep prisoners in line and working.\n\nAgents behave through finite state machines. They communicate via an internal messaging system, and have sensors to interact with the environment.\n\nAgent movement is managed through cost maps of different granularities, implementing the A* algorithm to calculate long routes. Closer or fixed paths are calculated via waypoints or random movement within an enclosed space.',
    repoUrl: 'https://github.com/mvrey/AICode'
  },
  {
    id: 'baoshi',
    title: 'Baoshi',
    technologies: ['C++', 'Unreal Engine'],
    imagePlaceholderId: 'baoshi-thumbnail',
    videoUrl: 'https://www.youtube.com/embed/9KeslerlgqQ',
    description: 'Game developed in Unreal Engine 4 as the final career project in Videogames Programming & Art.',
    longDescription: 'Game developed in Unreal Engine 4 as the final career project in Videogames Programming & Art. <br><br> It was created over the course of 7 months, by a team of 4 full-time 3D artists and 3 programmers, with completely exclusive assets and music.',
    downloadUrl: 'https://mvrey.es/baoshibuild1407.zip'
  },
];
